import Config from '../config/config';
import { log } from './debug_helper';
import AuthHelper from './auth_helper';
import { isEffectivelyEmpty } from './string_helper';
import Dispatcher from '../dispatchers/dispatcher';
import LocationHelper from './location_helper';
import { applicant, coApplicant } from '../components/customer/config/form_fields';
import * as ApplicationHelper from './application_helper';

let fetchId = 0;
const getFetchId = () => {
  if (!window.sessionStorage.getItem('fetchTraceId')) {
    // if none regenerate so theres always one
    let traceId = 'RA-' + Math.floor(Math.random() * 999999999999999).toString(16);
    window.sessionStorage.setItem('fetchTraceId', traceId);
  }
  return `${ApplicationHelper.isSelenium() ? 'WD-' : ''}-${window.sessionStorage.getItem(
    'fetchTraceId'
  )}-${(fetchId++).toString(16)}`;
};

function checkStatus(response) {
  if (response.status >= 200 && response.status < 400) {
    return response;
  } else {
    let json = {};
    try {
      return response
        .clone()
        .text()
        .then((errorText) => {
          json = errorText;
          try {
            json = JSON.parse(errorText);
          } catch (e) {}

          console.error('', errorText);

          const error = new Error(response?.statusText);
          error.response = response;
          throw Object.assign(error, { code: response?.status, body: json, messages: json?.messages });
        });
    } catch (e) {
      const error = new Error(response?.statusText);
      error.response = response;
      throw Object.assign(error, { code: response?.status, body: e.message });
    }
  }
}

function setOAuthEndpointOptions(path, options) {
  options.url = `${LocationHelper.getUrlForSubdomain('api')}/${path}`;
  options.headers['Content-Type'] = 'application/x-www-form-urlencoded';
  if (typeof options.body === 'object') {
    options.body = Object.keys(options.body)
      .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(options.body[k])}`)
      .join('&');
  }
}

function setRESTEndpointOptions(path, options) {
  options.url = `${LocationHelper.getUrlForSubdomain('api')}${Config.baseUri}${
    options.version || Config.version
  }/${path}`;
  options.headers['Content-Type'] = 'application/json';
  let search = LocationHelper.searchAsObj();
  if (search.envelopeId && search.signerRole && search.token) {
    options.headers['VerificationToken'] = search.token;
  }
  if (typeof options.body === 'object') {
    options.body = JSON.stringify(options.body, (k, v) => {
      if (v && (k === applicant.phone || k === coApplicant.phone)) {
        return v.replace(/\D/g, '').trim();
      }
      return typeof v === 'string' ? v.trim() : v;
    });
  }
}

const SPRUCE_IMPERSONATE_HEADER_NAME = 'spruce-impersonate';
export default class FetchHelper {
  static fetchJson(path, { headers = {}, ...options } = {}) {
    headers.accept = 'application/json';
    // headers['Accept-Encoding'] = 'gzip';

    if (options.isBytes) headers.accept += ', application/pdf';
    if (options.mimeType) headers.accept += `, ${options.mimeType}`;
    headers['spruce-ui'] = true;

    if (typeof path === 'string' && !path.includes('oauth/token') && !headers.authorization) {
      const oauthToken = AuthHelper.getSessionToken();
      if (oauthToken) headers.authorization = `Bearer ${oauthToken}`;
    }

    const impersonatedUserId = AuthHelper.getImpersonateCookie();
    if (impersonatedUserId && !headers[SPRUCE_IMPERSONATE_HEADER_NAME]) {
      headers[SPRUCE_IMPERSONATE_HEADER_NAME] = impersonatedUserId;
    }

    const recaptchaToken = Dispatcher.getStoreVal('recaptchaToken');
    if (!isEffectivelyEmpty(recaptchaToken)) {
      headers['recaptcha-token'] = recaptchaToken;
    }

    try {
      const customer = Dispatcher.getStoreVal('customer');
      const creditCheck = Dispatcher.getStoreVal('eligibility')?.creditCheck;
      const proposal = Dispatcher.getStoreVal('proposal');
      const financingMethod = Dispatcher.getStoreVal('financingMethod');
      const financingRequest = Dispatcher.getStoreVal('financingRequest');
      const user = Dispatcher.getStoreVal('user');
      const caseId = creditCheck?.externalId;

      headers['x-debug-cid'] = customer?.id;
      headers['x-debug-ccid'] = creditCheck?.id;
      headers['x-debug-pid'] = proposal?.id;
      headers['x-debug-fmid'] = financingMethod?.id;
      headers['x-debug-frid'] = financingRequest?.id;
      headers['x-debug-uid'] = user?.id;
      headers['x-debug-bid'] = caseId;
      headers['x-debug-uipage'] = window.location.href;
    } catch (e) {}

    headers['x-trace-id'] = getFetchId();

    if (!headers.authorization && AuthHelper.getCognitoCookie()) {
      headers.authorization = `Bearer ${AuthHelper.getCognitoCookie()}`;
    }

    options = {
      credentials: 'include',
      redirect: 'follow',
      mode: 'cors',
      cache: 'default',
      headers: headers,
      ...options,
    };
    if (path.startsWith('oauth')) {
      setOAuthEndpointOptions(path, options);
    } else {
      setRESTEndpointOptions(path, options);
    }
    let fetchPromise = fetch(FetchHelper.ifWebdriverPerformanceConcatMethodToUrl(options.url, options.method), options)
      .then((response) => {
        const { status, statusText } = response;
        log({
          url: options.url,
          method: options.method,
          requestBody: options.body,
          status,
          statusText,
        });
        return response;
      })
      .then(checkStatus);

    if (options.isBytes) {
      fetchPromise = fetchPromise.then((response) => ([204, 304].includes(response?.status) ? '{}' : response.blob()));
    } else {
      fetchPromise = fetchPromise
        .then((response) => {
          return [204, 304].includes(response?.status) ? '{}' : response?.text();
        })
        .then((text) => (text ? JSON.parse(text) : '{}'));
    }

    return fetchPromise;
  }

  static ifWebdriverPerformanceConcatMethodToUrl(url, method) {
    try {
      const webdriver =
        navigator.webdriver ||
        window.document.documentElement.getAttribute('webdriver') ||
        '_Selenium_IDE_Recorder' in window ||
        '__webdriver_script_fn' in document;

      if (!webdriver) {
        return url;
      }
      // if (url.includes('?')) {
      //   return `${url}&httpMethod=${method}`;
      // } else {
      //   return `${url}?httpMethod=${method}`;
      // }
    } catch (e) {}
    return url;
  }

  static uploadFile({
    path,
    uploadFile,
    headers = {},
    onUploadProgress = null,
    onUploadError = null,
    onUploadEnd = null,
    ...options
  } = {}) {
    headers['spruce-ui'] = true;

    const oauthToken = AuthHelper.getSessionToken();
    if (!headers.authorization && oauthToken) {
      headers.authorization = `Bearer ${oauthToken}`;
    }

    const impersonatedUserId = AuthHelper.getImpersonateCookie();

    if (impersonatedUserId && !headers[SPRUCE_IMPERSONATE_HEADER_NAME]) {
      headers[SPRUCE_IMPERSONATE_HEADER_NAME] = impersonatedUserId;
    }

    try {
      const customer = Dispatcher.getStoreVal('customer');
      const creditCheck = Dispatcher.getStoreVal('eligibility')?.creditCheck;
      const proposal = Dispatcher.getStoreVal('proposal');
      const financingMethod = Dispatcher.getStoreVal('financingMethod');
      const financingRequest = Dispatcher.getStoreVal('financingRequest');
      const user = Dispatcher.getStoreVal('user');
      const caseId = creditCheck?.externalId;

      headers['x-debug-cid'] = customer?.id;
      headers['x-debug-ccid'] = creditCheck?.id;
      headers['x-debug-pid'] = proposal?.id;
      headers['x-debug-fmid'] = financingMethod?.id;
      headers['x-debug-frid'] = financingRequest?.id;
      headers['x-debug-uid'] = user?.id;
      headers['x-debug-bid'] = caseId;
      headers['x-debug-uipage'] = window.location.href;
    } catch (e) {}

    headers['x-trace-id'] = getFetchId();

    if (!headers.authorization && AuthHelper.getCognitoCookie()) {
      headers.authorization = `Bearer ${AuthHelper.getCognitoCookie()}`;
    }

    options = {
      credentials: 'include',
      redirect: 'follow',
      mode: 'cors',
      method: 'POST',
      cache: 'default',
      headers: headers,
      ...options,
    };

    // 10,000,000 bytes = 10 megabytes
    // as of US510 we split large files into multiple chunks since UW lambda can only handle 6MB total response size anyway
    if (uploadFile.filesize > 24999999) {
      if (typeof onUploadProgress === 'function') {
        onUploadProgress({ percent: -1 });
      }
      if (typeof onUploadError === 'function') {
        const response = new Response('{"messages": ["File Contents exceed limit of 25MB"]}'); // This is dumb
        onUploadError({ error: { response: response } });
      }
      return;
    }

    options.body = uploadFile;
    setRESTEndpointOptions(path, options);
    if (typeof onUploadProgress === 'function') {
      onUploadProgress({ percent: 0 });
    }
    let uploadPromise = fetch(options.url, options)
      .then((response) => response.json())
      .then((json) => {
        if (json.fieldErrors.length > 0 && typeof onUploadError === 'function') {
          json.fieldErrors.forEach((err) => {
            onUploadError({ error: err });
            onUploadProgress({ percent: -1 });
          });
        } else {
          if (typeof onUploadProgress === 'function') {
            onUploadProgress({ percent: 100 });
          }
          if (typeof onUploadEnd === 'function') {
            onUploadEnd({ res: json, success: true });
          }
        }
        return json;
      })
      .catch((error) => {
        if (typeof onUploadProgress === 'function') {
          onUploadProgress({ percent: -1 });
        }
        if (error) {
          if (typeof onUploadError === 'function') {
            onUploadError({ error });
          }
          throw error;
        }
      });
    return uploadPromise;
  }

  static async generate({
    path,
    headers = {},
    onGenerateProgress = null,
    onGenerateError = null,
    onGenerateEnd = null,
    ...options
  } = {}) {
    headers['spruce-ui'] = true;
    const oauthToken = AuthHelper.getSessionToken();

    if (oauthToken) headers.authorization = `Bearer ${oauthToken}`;

    headers.accept = 'application/json';

    headers['x-trace-id'] = getFetchId();
    const impersonatedUserId = AuthHelper.getImpersonateCookie();
    if (impersonatedUserId && !headers[SPRUCE_IMPERSONATE_HEADER_NAME]) {
      headers[SPRUCE_IMPERSONATE_HEADER_NAME] = impersonatedUserId;
    }

    options = {
      method: 'POST',
      credentials: 'include',
      redirect: 'follow',
      mode: 'cors',
      cache: 'default',
      headers: headers,
      ...options,
    };

    setRESTEndpointOptions(path, options);
    let onProgress = typeof onGenerateProgress === 'function' ? onGenerateProgress : () => {};
    let onError = typeof onGenerateError === 'function' ? onGenerateError : () => {};
    let onEnd = typeof onGenerateEnd === 'function' ? onGenerateEnd : () => {};

    onProgress({ percent: 0 });

    if (!options.headers) {
      options.headers = {};
    }
    options.headers['x-trace-id'] = getFetchId();

    try {
      let res = await fetch(options.url, options).then(checkStatus);
      onProgress({ percent: 100 });
      onEnd({ res, success: true });
      return await res.json();
    } catch (error) {
      onProgress({ percent: 100 });
      if (error) {
        onError({ error });
        throw error;
      }
    }
  }
}
